// Launch rehearsal signals (Module 37).
//
// Aggregates whether each rehearsal step has produced its expected
// real-world signal yet. All read-only, all admin-RLS-permitted, no
// new tables. Deliberately small queries — designed to run in parallel
// once when the rehearsal page loads, and again on the manual Refresh.

import { supabase, isSupabaseConfigured } from "./supabase";
import { ROLES } from "./roles";
import { loadPushCoverage } from "./systemHealth";

function ensureLive() {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
}

function isoTodayStart() {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * Returns the live state for every rehearsal step. Shape:
 *   {
 *     parent: { push_active, message_sent, has_inquiry },
 *     teacher: { push_active, daily_report_today, photo_uploaded_today },
 *     admin: { public_page_live, system_widget_seen, moderation_clear, email_sending },
 *   }
 *
 * Each value is a boolean. The page maps them to ok / pending state and
 * never claims `done` for steps the data can't reach.
 */
export async function loadRehearsalSignals() {
  ensureLive();
  const todayStart = isoTodayStart();

  const [
    pushCoverage,
    messageProbe,
    inquiryProbe,
    dailyReportProbe,
    photoTodayProbe,
    pageProbe,
    moderationPending,
    notificationsProbe,
  ] = await Promise.all([
    loadPushCoverage().catch(() => null),
    supabase
      .from("messages")
      .select("id", { count: "exact", head: true })
      .limit(1),
    supabase
      .from("enrollment_inquiries")
      .select("id", { count: "exact", head: true })
      .limit(1),
    supabase
      .from("daily_reports")
      .select("id", { count: "exact", head: true })
      .eq("report_date", todayStart.slice(0, 10))
      .limit(1),
    supabase
      .from("child_photos")
      .select("id", { count: "exact", head: true })
      .gte("created_at", todayStart)
      .limit(1),
    supabase
      .schema("cms")
      .from("pages")
      .select("id", { count: "exact", head: true })
      .eq("status", "published")
      .limit(1),
    supabase
      .from("child_photos")
      .select("id", { count: "exact", head: true })
      .eq("moderation_status", "flagged")
      .limit(1),
    supabase
      .from("notifications_log")
      .select("id", { count: "exact", head: true })
      .eq("status", "sent")
      .limit(1),
  ]);

  const messagesCount = messageProbe?.count || 0;
  const inquiriesCount = inquiryProbe?.count || 0;
  const dailyReportsToday = dailyReportProbe?.count || 0;
  const photosToday = photoTodayProbe?.count || 0;
  const pagesLive = pageProbe?.count || 0;
  const moderationPendingCount = moderationPending?.count || 0;
  const notificationsSent = notificationsProbe?.count || 0;

  return {
    parent: {
      push_active: (pushCoverage?.[ROLES.PARENT]?.opted_in || 0) > 0,
      message_sent: messagesCount > 0,
      has_inquiry: inquiriesCount > 0,
    },
    teacher: {
      push_active: (pushCoverage?.[ROLES.TEACHER]?.opted_in || 0) > 0,
      daily_report_today: dailyReportsToday > 0,
      photo_uploaded_today: photosToday > 0,
    },
    admin: {
      public_page_live: pagesLive > 0,
      moderation_clear: moderationPendingCount === 0,
      email_sending: notificationsSent > 0,
      // System widget seen — proxied by "the admin loaded /admin/system at
      // least once". We can't probe localStorage from a query, so admins
      // self-confirm this on the rehearsal page.
    },
  };
}

/**
 * Total counts: how many rehearsal items are live-confirmed vs total.
 * Used by the launch-day confidence summary on the dashboard.
 */
export function rehearsalScore(signals) {
  if (!signals) return { ready: 0, total: 0 };
  let ready = 0;
  let total = 0;
  for (const persona of ["parent", "teacher", "admin"]) {
    for (const v of Object.values(signals[persona] || {})) {
      total += 1;
      if (v) ready += 1;
    }
  }
  return { ready, total };
}
